import * as R from 'rambdax'
import {PartialDeep} from 'type-fest'
import {Falsish} from './types'

/**
 * Deep object assign. Values in the right-side source are ignored if pred
 * returns false. Mutates target object. Not exported.
 */
const deepAssign = <T extends object>(
  tgt: T,
  src: PartialDeep<T>,
  pred: (v: unknown) => boolean,
): T => {
  for (const [k, v] of Object.entries(src)) {
    if (!pred(v)) continue
    const ov = tgt[k as keyof typeof tgt]
    tgt[k as keyof typeof tgt] =
      !v || typeof v !== 'object'
        ? v
        : !ov || typeof ov !== 'object' || Array.isArray(v) || Array.isArray(ov)
          ? R.clone(v)
          : deepAssign(ov, v, pred)
  }
  return tgt
}

/**
 * Deep object merge. Undefined values in the right-side sources are ignored
 * rather than merged. Returns a new object, deeply cloned from all sources.
 */
export const deepMerge =
  <T extends object>(
    def: T,
    pred: (v: unknown) => boolean = v => v !== undefined,
  ) =>
  (...srcs: Array<PartialDeep<T> | Falsish>): T => {
    const tgt = R.clone(def)
    for (const src of srcs) {
      if (src) deepAssign(tgt, src, pred)
    }
    return tgt
  }
